import { useState } from 'react';
import { Search, MapPin, Truck, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const RADIUS_OPTIONS = [25, 50, 100, 150, 200];
const TRUCK_TYPES = ['All', 'Reefer', 'Dry Van'];
const POST_AGE_OPTIONS = [
  { label: 'Any', value: '' },
  { label: 'Last 2 hours', value: '2h' },
  { label: 'Last 12 hours', value: '12h' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 3 days', value: '3d' },
];

export default function LoadSearchForm({ onSearch, loading }) {
  const [origin, setOrigin] = useState('');
  const [radius, setRadius] = useState(50);
  const [destination, setDestination] = useState('');
  const [anyDestination, setAnyDestination] = useState(true);
  const [truckType, setTruckType] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [postAge, setPostAge] = useState('');

  const handleSearch = () => {
    onSearch({ origin, radius, destination: anyDestination ? '' : destination, truckType, startDate, endDate, postAge });
  };

  const handleDestinationToggle = () => {
    setAnyDestination(prev => !prev);
    if (!anyDestination) setDestination('');
  };

  return (
    <div className="glass-card p-5 space-y-4">
      <h2 className="font-space font-semibold text-foreground text-sm uppercase tracking-wide flex items-center gap-2">
        <Search className="w-4 h-4 text-primary" /> Search Loads
      </h2>

      {/* Row 1: Origin + Radius + Destination */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
        {/* Origin */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Origin (City, State)
          </label>
          <Input
            value={origin}
            onChange={e => setOrigin(e.target.value)}
            placeholder="e.g. Las Vegas, NV"
            className="text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {/* Deadhead Radius */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Deadhead (mi)</label>
          <select
            value={radius}
            onChange={e => setRadius(Number(e.target.value))}
            className="w-full px-3 py-2 h-9 rounded-md bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {RADIUS_OPTIONS.map(r => (
              <option key={r} value={r} className="bg-background">{r} mi</option>
            ))}
          </select>
        </div>

        {/* Destination */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Destination
            <button
              onClick={handleDestinationToggle}
              className={cn(
                "ml-auto text-xs px-2 py-0.5 rounded-full border transition-colors",
                anyDestination
                  ? "bg-primary/20 border-primary/40 text-primary"
                  : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"
              )}
            >
              Anywhere
            </button>
          </label>
          <Input
            value={destination}
            onChange={e => setDestination(e.target.value)}
            placeholder={anyDestination ? 'Any destination' : 'e.g. Phoenix, AZ'}
            disabled={anyDestination}
            className="text-foreground placeholder:text-muted-foreground disabled:opacity-40"
          />
        </div>
      </div>

      {/* Row 2: Truck Type + Dates + Post Age */}
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_auto] gap-3 items-end">
        {/* Truck Type */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Truck className="w-3 h-3" /> Truck Type
          </label>
          <div className="flex gap-1">
            {TRUCK_TYPES.map(t => (
              <button
                key={t}
                onClick={() => setTruckType(t)}
                className={cn(
                  "px-3 py-2 h-9 rounded-md text-sm font-medium transition-all border",
                  truckType === t
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted/40 text-muted-foreground border-border hover:bg-white/10 hover:text-foreground"
                )}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Start Date */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Pickup From
          </label>
          <Input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="text-foreground"
          />
        </div>

        {/* End Date */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Pickup To
          </label>
          <Input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="text-foreground"
          />
        </div>

        {/* Post Age */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /> Post Age
          </label>
          <select
            value={postAge}
            onChange={e => setPostAge(e.target.value)}
            className="w-full px-3 py-2 h-9 rounded-md bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {POST_AGE_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className="bg-background">{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Search Button */}
      <Button
        onClick={handleSearch}
        disabled={loading}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-10"
      >
        <Search className="w-4 h-4 mr-2" />
        {loading ? 'Searching...' : 'Search Loads'}
      </Button>
    </div>
  );
}